import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import { message, notification, Table, Button, Dropdown, Menu, Upload, Pagination, ConfigProvider, Spin, Card, Row, Col, Tag, Tooltip, Modal, Checkbox, Popover } from "antd";
import { DownloadOutlined, CloudUploadOutlined, PlusOutlined, EyeOutlined, InfoCircleOutlined, SettingOutlined, FilterOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import AddExpense from "./lib/AddExpense";
import ExpenseFilterModal from "./lib/ExpenseFilterModal";
import { setData as setDataAction } from "../../../redux/actions";
import SyncToast from '../../../components/toast/SyncToast';
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay";
import {useDispatch} from "react-redux";
import { AddCommas } from "../../../config";
import { useHubSpotSync } from "../../../hooks/useHubSpotSync";
import { useVisibleColumns } from "../../../hooks/useVisibleColumns";
import { COLUMN_PREF_PAGES } from "../../../utils/columnPreferences";
import { useCanEdit } from "../../../contexts/SubUserAccessContext";

const sanitizeAsin = (s) => {
    if (s === null || s === undefined) return "";
    let str = String(s).trim();
    str = str.replace(/^['"TM"“”‘’`]+|['"TM"“”‘’`]+$/g, "").trim();
    return str;
};

// Custom hook to get window size
const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

const Expenses = (props) => {
    const { userData, setData } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { syncActionTimestamp } = useHubSpotSync(userData);

    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const canEdit = useCanEdit();
    const canMutateExpenses = isSpApiConnected && canEdit;
    const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;
    const trialExpired = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);

    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [marketplaceList, setMarketplaceList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [sortParam, setSortParam] = useState("");

    // API list filters — same model as Products / Order Items (column + operator + value)
    const [activeFilters, setActiveFilters] = useState([]);
    const [categoryFilterOptions, setCategoryFilterOptions] = useState([]);
    const [productFilterOptions, setProductFilterOptions] = useState([]);
    const [productFilterLoading, setProductFilterLoading] = useState(false);
    const productFilterSearchRef = useRef(null);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const addFilter = useCallback((filterObj) => {
        setActiveFilters((prev) => [...prev, filterObj]);
        setPage(1);
    }, []);

    const removeFilter = useCallback((id) => {
        setActiveFilters((prev) => prev.filter((f) => f.id !== id));
        setPage(1);
    }, []);

    const updateFilter = useCallback((id, updatedFields) => {
        setActiveFilters((prev) => prev.map((f) => (f.id === id ? { ...f, ...updatedFields } : f)));
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        if (productFilterSearchRef.current) clearTimeout(productFilterSearchRef.current);
        setActiveFilters([]);
        setProductFilterOptions([]);
        setPage(1);
    }, []);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);

    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [templateDownloading, setTemplateDownloading] = useState(false);
    const [syncToastDismissed, setSyncToastDismissed] = useState(false);
    
    // Modal state for mobile view
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);

    const searchDebounceRef = useRef(null);
    const prevSearchRef = useRef("");

    // Get window size and determine if mobile
    const windowSize = useWindowSize();
    const isMobile = windowSize.width < 768;

    useEffect(() => {
        if (isDataSyncing) setSyncToastDismissed(false);
    }, [isDataSyncing]);

    const loadExpenseCategoriesForFilter = useCallback(async () => {
        if (!isSpApiConnected || !userData?.token) return;
        try {
            const response = await MakeApiCall("get-expense-categories?no_pagination=true", "get", null, true, {}, userData.token);
            if (response?.status && Array.isArray(response?.data?.records)) {
                setCategoryFilterOptions(
                    response.data.records.map((c) => ({ value: c.name, label: c.name }))
                );
            }
        } catch (e) {
            console.error("Error loading expense categories for filters:", e);
        }
    }, [isSpApiConnected, userData?.token]);

    const searchProductsForFilter = useCallback(
        (rawQuery) => {
            if (!isSpApiConnected || !userData?.token) return;
            if (productFilterSearchRef.current) clearTimeout(productFilterSearchRef.current);
            productFilterSearchRef.current = setTimeout(async () => {
                const q = sanitizeAsin(rawQuery || "");
                setProductFilterLoading(true);
                try {
                    let url = "get-products?page=1&per-page=20";
                    if (q) url += `&search_text=${encodeURIComponent(q)}`;
                    const response = await MakeApiCall(url, "get", null, true, {}, userData.token);
                    if (response?.status) {
                        const records = response.data?.records || [];
                        const seen = new Set();
                        const opts = [];
                        for (const r of records) {
                            const asin = sanitizeAsin(r.asin || r.id);
                            if (!asin || seen.has(asin)) continue;
                            seen.add(asin);
                            const sku = r.sku || "";
                            const label = [sku || asin, r.item_name].filter(Boolean).join(" — ") || asin || sku;
                            opts.push({ value: asin, label: label || asin });
                        }
                        setProductFilterOptions(opts);
                    } else {
                        setProductFilterOptions([]);
                    }
                } catch (err) {
                    setProductFilterOptions([]);
                } finally {
                    setProductFilterLoading(false);
                }
            }, 300);
        },
        [isSpApiConnected, userData?.token]
    );

    useEffect(() => {
        if (isSpApiConnected) loadExpenseCategoriesForFilter();
    }, [isSpApiConnected, loadExpenseCategoriesForFilter]);

    const getExpensesList = useCallback(async (data) => {
        if (!isSpApiConnected) { setLoading(false); return; }
        setLoading(true);

        const st = data?.searchText !== undefined ? data.searchText : searchText;
        const currentPage = data?.page !== undefined && data?.page !== null ? data.page : page || 1;
        const currentPageSize = data?.pageSize !== undefined && data?.pageSize !== null ? data.pageSize : pageSize;
        const currentSort = data?.sortParam !== undefined ? data.sortParam : sortParam;

        let url = `get-expenses?search_text=${encodeURIComponent(st ?? "")}&page=${currentPage}&per-page=${currentPageSize}`;
        if (currentSort) {
            url += `&sort=${encodeURIComponent(currentSort)}`;
        }
        for (const f of activeFilters) {
            if (f.column === "type" && f.value != null && f.value !== "") {
                url += `&type=${encodeURIComponent(String(f.value))}`;
            }
            if (f.column === "name" && f.value != null && String(f.value).trim() !== "") {
                url += `&name=${encodeURIComponent(String(f.value).trim())}`;
            }
            if (f.column === "category" && f.value != null && f.value !== "") {
                url += `&category=${encodeURIComponent(String(f.value))}`;
            }
            if (f.column === "show_under_advertising_cost" && (f.value === "0" || f.value === "1" || f.value === 0 || f.value === 1)) {
                const bit = f.value === 1 || f.value === "1" ? "1" : "0";
                url += `&show_under_advertising_cost=${bit}`;
            }
            if (f.column === "products" && f.value != null && String(f.value).trim() !== "") {
                url += `&products=${encodeURIComponent(String(f.value).trim())}`;
            }
        }

        const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);

        if (response?.status) {
            const records = response?.data?.records || [];
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            setPage(response?.data?.pagination?.page || 1);
            setList(records);
            setLoading(false);

            // Do not flip onboarding flags when the list is empty only because of active filters —
            // expenses may still exist; avoids "Add expense" / COGS & Tax banner flashing incorrectly.
            if (userData && activeFilters.length === 0) {
                try {
                    const hasExpenses = Array.isArray(records) && records.length > 0;
                    if (userData?.user?.user_data?.is_expense_data_added !== hasExpenses) {
                        const newUserData = {
                            ...userData,
                            user: { ...userData.user, user_data: { ...userData.user.user_data, is_expense_data_added: hasExpenses } },
                        };
                        if (typeof setData === 'function') setData(newUserData);
                        if (typeof dispatch === 'function') dispatch(setDataAction(newUserData));
                    }
                } catch (e) { console.error('Error updating userData:', e); }
            }
        } else {
            message.warning(response?.message);
            setLoading(false);
            setList([]);
        }
    }, [
        isSpApiConnected,
        searchText,
        page,
        pageSize,
        userData,
        dispatch,
        setData,
        sortParam,
        activeFilters,
    ]);

    const handleTableChange = (pagination, filters, sorter) => {
        let newSortParam = "";
        const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

        if (activeSorter.order) {
            newSortParam = activeSorter.order === "ascend" ? activeSorter.field : `-${activeSorter.field}`;
        }

        setSortParam(newSortParam);
        setPage(1);
    };

    const MarketplaceListAction = useCallback(async () => {
        if (!isSpApiConnected) return;
        setLoading(true);
        const response = await MakeApiCall(`get-marketplaces`, `get`, null, true, null, userData?.token);
        if (response?.status) {
            setMarketplaceList(response.data.all_marketplaces?.map((d) => ({
                label: d?.sales_channel,  // e.g. "Amazon.com.mx"
                value: d?.id,             // e.g. "A1AM78C64UM0Y8"
                name: d?.name,            // e.g. "Mexico" — used for search in AddExpense
            })) || []);
        }
        setLoading(false);
    }, [isSpApiConnected, userData]);

    useEffect(() => {
        if (!isSpApiConnected) { setLoading(false); setList([]); return; }

        const isSearchChange = prevSearchRef.current !== searchText;
        prevSearchRef.current = searchText;

        if (isSearchChange) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                getExpensesList({ page: 1, pageSize, searchText, sortParam });
            }, 400);
            return () => clearTimeout(searchDebounceRef.current);
        }

        getExpensesList({ page, pageSize, searchText, sortParam });
    }, [getExpensesList, isSpApiConnected, page, pageSize, searchText, sortParam, activeFilters]);

    useEffect(() => { if (isSpApiConnected) MarketplaceListAction(); }, [MarketplaceListAction, isSpApiConnected]);

    const getSortOrder = (columnKey) => {
        return sortParam === columnKey ? 'ascend' : sortParam === `-${columnKey}` ? 'descend' : null;
    };

    // Render detail modal for mobile view
    const renderDetailModal = () => {
        if (!selectedRecord) return null;
        
        return (
            <Modal
                className="expenses-detail-modal"
                title="Expense Details"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={false}
                width={isMobile ? '95%' : 600}
                bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
            >
                <div className="expense-detail-modal">
                    <div className="detail-section">
                        <div className="detail-row">
                            <span className="detail-label">Date:</span>
                            <span className="detail-value">{selectedRecord.start_date || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Type:</span>
                            <span className="detail-value">{selectedRecord.type || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Name:</span>
                            <span className="detail-value fw-bold">{selectedRecord.name || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Category:</span>
                            <span className="detail-value">
                                <Tag color="blue">{selectedRecord.category || '-'}</Tag>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Advertising Cost:</span>
                            <span className="detail-value">
                                <Tag color={selectedRecord.show_under_advertising_cost ? 'green' : 'default'}>
                                    {selectedRecord.show_under_advertising_cost ? 'Yes' : 'No'}
                                </Tag>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Product:</span>
                            <span className="detail-value">
                                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    {selectedRecord.products || '-'}
                                </div>
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Marketplace:</span>
                            <span className="detail-value">{selectedRecord.marketplace_ids || '-'}</span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Amount:</span>
                            <span className="detail-value fw-bold text-primary">
                                {selectedRecord.currency} {AddCommas(selectedRecord.amount || '-')}
                            </span>
                        </div>
                        
                        <div className="detail-row">
                            <span className="detail-label">Tax:</span>
                            <span className="detail-value">
                                {selectedRecord.tax != null ? `${selectedRecord.tax}%` : '-'}
                            </span>
                        </div>
                        
                        {/* {selectedRecord.end_date && (
                            <div className="detail-row">
                                <span className="detail-label">End Date:</span>
                                <span className="detail-value">{selectedRecord.end_date}</span>
                            </div>
                        )}
                        
                        {selectedRecord.no_of_occurrences && (
                            <div className="detail-row">
                                <span className="detail-label">Occurrences:</span>
                                <span className="detail-value">{selectedRecord.no_of_occurrences}</span>
                            </div>
                        )} */}
                        
                        <div className="detail-row">
                            <span className="detail-label">Amortize Daily:</span>
                            <span className="detail-value">
                                <Tag color={selectedRecord.amortize_expense_daily ? 'green' : 'default'}>
                                    {selectedRecord.amortize_expense_daily ? 'Yes' : 'No'}
                                </Tag>
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    // Render card view for mobile
    const renderCardView = () => {
        if (loading) {
            return <div className="text-center py-5"><Spin /></div>;
        }

        if (list.length === 0) {
            return <div className="text-center py-5">No expenses found</div>;
        }

        return (
            <div className="expense-cards-container">
                {list.map((record, index) => (
                    <Card 
                        key={record.id}
                        className="mb-3"
                        style={{ 
                            borderRadius: '8px',
                            borderLeft: `0px solid ${record.show_under_advertising_cost ? '#f45b68' : '#1890ff'}`,
                            backgroundColor: '#fff'
                        }}
                    >
                        <div className="card-content">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="fw-bolder text-dark fs-6">
                                    #{((page - 1) * pageSize + index + 1)} - {record.name}
                                </div>
                                <div>
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                            setSelectedRecord(record);
                                            setDetailModalVisible(true);
                                        }}
                                        icon={<EyeOutlined />}
                                        className="me-1"
                                    />
                                    <Dropdown overlay={menu1(record)} disabled={!canMutateExpenses}>
                                        <Button
                                            type="link"
                                            size="small"
                                            disabled={!canMutateExpenses}
                                            icon={<i className="ki-solid ki-dots-vertical fs-3"></i>}
                                        />
                                    </Dropdown>
                                </div>
                            </div>
                            
                            {/* Main Fields - 5 Key Fields */}
                            <Row gutter={[8, 8]} className="mb-2">
                                <Col span={12}>
                                    <div className="fs-7">
                                        <div className="text-muted">Date:</div>
                                        <div className="ms-0 fw-semibold">{record.start_date || '-'}</div>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div className="fs-7">
                                        <div className="text-muted">Type:</div>
                                        <div className="ms-0 fw-semibold">{record.type || '-'}</div>
                                    </div>
                                </Col>
                            </Row>
                            
                            <Row gutter={[8, 8]} className="mb-2">
                                
                                <Col span={24}>
                                    <div className="fs-7">
                                        <span className="text-muted">Amount:</span>
                                        <span className="ms-2 fw-bold text-primary">
                                            {record.currency} {record.amount || '-'}
                                        </span>
                                    </div>
                                </Col>
                                <Col span={24}>
                                    <div className="fs-7">
                                        <span className="text-muted">Category:</span>
                                        <span className="ms-2">
                                            <Tag color="blue">{record.category || '-'}</Tag>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            
                            {/* <div className="mb-2">
                                <div className="fs-7">
                                    <span className="text-muted">Marketplace:</span>
                                    <span className="ms-2">{record.marketplace_ids || '-'}</span>
                                </div>
                            </div> */}
                            
                            <div className="mb-2">
                                <div className="fs-7">
                                    <span className="text-muted">Ad Cost:</span>
                                    <span className="ms-2">
                                        <Tag color={record.show_under_advertising_cost ? 'green' : 'default'}>
                                            {record.show_under_advertising_cost ? 'Yes' : 'No'}
                                        </Tag>
                                    </span>
                                </div>
                            </div>
                            
                            {/* View Details Button */}
                            <Button 
                                type="link" 
                                className="p-0 mt-2"
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setDetailModalVisible(true);
                                }}
                            >
                                View all details <i className="ki-outline ki-arrow-right fs-6 ms-1"></i>
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    const colTitle = (label, tip) => (
        <div className="d-flex align-items-center">
            {label}
            <Tooltip title={tip} placement="top">
                <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '12px', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
        </div>
    );

    const renderAsinPopover = (products) => {
        let items = [];
        if (!products) return <Tag>N/A</Tag>;
        try {
            if (typeof products === 'string') {
                // Try to parse as JSON array, fallback to comma split
                try {
                    const parsed = JSON.parse(products);
                    if (Array.isArray(parsed)) items = parsed;
                    else items = products.split(',').map(s => s.trim()).filter(Boolean);
                } catch (e) {
                    items = products.split(',').map(s => s.trim()).filter(Boolean);
                }
            } else if (Array.isArray(products)) {
                items = products;
            }
        } catch (e) {
            return <Tag>N/A</Tag>;
        }
        // Remove empty strings, stray quotes, and trim each item
        items = items
            .map(item => (typeof item === 'string' ? item.trim().replace(/^"+|"+$/g, '') : item))
            .filter(item => !!item && item !== '"' && item !== "''" && item !== '""');
        if (!Array.isArray(items) || items.length === 0) {
            return <Tag>N/A</Tag>;
        }
        if (items.length <= 2) {
            return items.map((item) => <Tag key={item}>{item}</Tag>);
        }
        const visibleAsins = items.slice(0, 2);
        const hiddenAsins = items.slice(2);
        return (
            <>
                {visibleAsins.map((item) => <Tag key={item}>{item}</Tag>)}
                <Popover
                    content={<div>{hiddenAsins.map((item) => <Tag key={item}>{item}</Tag>)}</div>}
                    title="More ASINs"
                >
                    <Tag color="blue" style={{ cursor: 'pointer' }}>+{hiddenAsins.length}</Tag>
                </Popover>
            </>
        );
    };

    const allColumns = useMemo(() => [
        { title: "#", label: "#", key: "index", render: (text, record, index) => (page - 1) * pageSize + index + 1 },
        {
            title: colTitle("Date", "Date the expense was incurred."), label: "Date", dataIndex: "start_date", key: "start_date",
            sorter: true,
            sortOrder: getSortOrder("start_date")
        },
        {
            title: colTitle("Type", "Whether the expense is recurring or one-time."), label: "Type", dataIndex: "type", key: "type",
            render: (type) => (type !== null ? type : "-"),
            sorter: true,
            sortOrder: getSortOrder("type")
        },
        {
            title: colTitle("Name", "Label or title you've given this expense."), label: "Name", dataIndex: "name", key: "name",
            render: (name) => (name !== null ? name : "-"),
            sorter: true,
            sortOrder: getSortOrder("name")
        },
        {
            title: colTitle("Category", "Expense category (e.g., logistics, software, marketing) for reporting and grouping."), label: "Category", dataIndex: "category", key: "category",
            render: (category) => (category !== null ? category : "-"),
            sorter: true,
            sortOrder: getSortOrder("category")
        },
        {
            title: colTitle("Advertising Cost", "Ad spend linked to this expense entry — pulled or mapped to your campaigns."), label: "Advertising Cost", dataIndex: "show_under_advertising_cost", key: "show_under_advertising_cost",
            render: (val) => val ? "Yes" : "No",
            sorter: true,
            sortOrder: getSortOrder("show_under_advertising_cost")
        },
        { title: colTitle("Product", "SKU or product this expense is attributed to, if applicable."), label: "Product", dataIndex: "products", key: "products", render: renderAsinPopover },
        { title: colTitle("Marketplace", "The marketplace (e.g., Amazon IN, Amazon US) this expense applies to."), label: "Marketplace", dataIndex: "marketplace_ids", key: "marketplace_ids", render: (m) => m || "-" },
        {
            title: colTitle("Amount", "Total expense value before tax."), label: "Amount", dataIndex: "amount", key: "amount",
            render: (_, r) => `${r?.currency} ${AddCommas(r?.amount ?? "-")}`,
            sorter: true,
            sortOrder: getSortOrder("amount")
        },

        {
            title: colTitle("Tax", "Tax component of this expense — used for input credit calculation."), label: "Tax", dataIndex: "tax", key: "tax",
            render: (_, r) => r?.tax != null ? `${r.tax}%` : "-",
            sorter: true,
            sortOrder: getSortOrder("tax")
        },
        { title: "Action", label: "Action", key: "action", render: (_, row) => (
                <Dropdown overlay={menu1(row)} disabled={!canMutateExpenses}>
                    <Button type="link" disabled={!canMutateExpenses}><i className="ki-solid ki-dots-vertical fs-1 text-gray-800 "></i></Button>
                </Dropdown>
            )},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [page, pageSize, sortParam, canMutateExpenses, canEdit]);

    // ─── Column visibility ────────────────────────────────────────────────────
    // Fixed (always visible, not toggleable): index, name, action
    const FIXED_COLUMNS = useMemo(() => new Set(['index', 'name', 'action']), []);

    const toggleableColumns = useMemo(
        () => allColumns.filter(col => !FIXED_COLUMNS.has(col.key)),
        [allColumns, FIXED_COLUMNS]
    );

    const userId = userData?.user?.user_data?.id ?? userData?.user?.id;
    const allColumnKeys = useMemo(() => allColumns.map(col => col.key), [allColumns]);

    const { visibleColumns, handleColumnToggle } = useVisibleColumns({
        pageId: COLUMN_PREF_PAGES.EXPENSES,
        userId,
        allColumnKeys,
        fixedColumnKeys: ['index', 'name', 'action'],
        hiddenByDefaultKeys: [],
    });

    const columns = useMemo(
        () => allColumns.filter(col => visibleColumns.includes(col.key)),
        [allColumns, visibleColumns]
    );

    // ─── Column selector dropdown content ────────────────────────────────────
    const allToggleableKeys = toggleableColumns.map(c => c.key);
    const allToggleableKeysSet = new Set(allToggleableKeys);
    const checkedKeys = visibleColumns.filter(k => allToggleableKeysSet.has(k));
    const checkAll      = checkedKeys.length === allToggleableKeys.length;
    const indeterminate = checkedKeys.length > 0 && checkedKeys.length < allToggleableKeys.length;

    const columnSelectorOverlay = (
        <div className="card shadow p-4" style={{ maxHeight: '400px', overflowY: 'auto', minWidth: '220px' }}>
            <div className="fw-bold mb-3 border-bottom pb-2">Display Columns</div>
            <div className="mb-2 pb-2 border-bottom">
                <Checkbox
                    indeterminate={indeterminate}
                    checked={checkAll}
                    onChange={(e) => handleColumnToggle(e.target.checked ? allToggleableKeys : [])}
                >
                    <span className="fw-semibold">Select All</span>
                </Checkbox>
            </div>
            <Checkbox.Group
                options={toggleableColumns.map(c => ({ label: c.label || (typeof c.title === 'string' ? c.title : c.dataIndex), value: c.key }))}
                value={checkedKeys}
                onChange={handleColumnToggle}
                className="d-flex flex-column gap-2"
            />
        </div>
    );

    const handleSearch = (e) => { setSearchText(e.target.value); setPage(1); };

    // ── Shared helper — mirrors the Products export notification exactly ───────
    const showExportNotification = (data, serverMessage) => {
        const fileId = data?.file_id ?? data?.export_file_id ?? "";
        const key = `export-notif-${Date.now()}`;
        notification.success({
            key,
            className: "expenses-export-notif",
            message: "Export started",
            description: (
                <span>
                    {fileId && <><b>{fileId}</b> — </>}
                    {serverMessage ?? "Your file is being prepared."}{" "}
                    <a
                        href="/settings/file-manager?tab=export"
                        style={{ color: "#f45b68", fontWeight: 600 }}
                        onClick={(e) => {
                            e.preventDefault();
                            notification.destroy(key);
                            navigate("/settings/file-manager?tab=export");
                        }}
                    >
                        Go to Import & Export
                    </a>
                </span>
            ),
            duration: 0,
            placement: "top",
            style: { width: "max-content" },
        });
    };

    const handleExport = async () => {
        if (!isSpApiConnected || exporting) return;
        syncActionTimestamp("sv_used_import_export");
        setExporting(true);
        try {
            const response = await MakeApiCall("expenses/export", "post", null, true, {}, userData?.token);
            if (response?.status) {
                showExportNotification(response.data, response.message);
            } else {
                message.error(response?.message || "Export failed. Please try again.");
            }
        } catch (err) {
            console.error("Expenses export error:", err);
            message.error("Something went wrong while starting the export.");
        } finally {
            setExporting(false);
        }
    };

    const handleCancel = () => setIsModalVisible(false);

    const handleSubmit = async (values) => {
        if (!canMutateExpenses) return;
        setLoading(true);
        const response = await MakeApiCall(editingExpense ? `update-expense/${editingExpense.id}` : "add-expense", "post", values, true, {}, userData?.token);
        if (response?.status) {
            message.success("Saved!");
            setIsModalVisible(false);
            getExpensesList();
        }
        setLoading(false);
    };

    const handleEdit = (expense) => { setEditingExpense(expense); setIsModalVisible(true); };
    const handleDelete = async (id) => {
        if (!canMutateExpenses) return;
        setLoading(true);
        const response = await MakeApiCall(`delete-expense/${id}`, "delete", null, true, {}, userData?.token);
        if (response?.status) { message.success("Deleted!"); getExpensesList(); }
        setLoading(false);
    };

    const menu1 = (row) => (
        <Menu className="min-w-200px p-3">
            <Menu.Item key="1" className="d-flex align-items-center mb-2" onClick={() => handleEdit(row)}>
                <i className="ki-outline ki-pencil fs-3 me-2"></i>Edit Expenses
            </Menu.Item>
            <Menu.Item key="2" className="d-flex align-items-center" onClick={() => handleDelete(row?.id)}>
                <i className="ki-outline ki-trash fs-3 me-2"></i>Delete Expenses
            </Menu.Item>
        </Menu>
    );

    const handleUpload = (file) => {
        if (!canMutateExpenses) return false;
        const rawFile = file.originFileObj || file;
        setUploadedFile(rawFile);
        message.success(`${rawFile.name} ready.`);
        return false;
    };

    const handleApplyClick = async () => {
        if (!canMutateExpenses || !uploadedFile || uploading) return;
        syncActionTimestamp("sv_used_import_export");
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            const response = await MakeApiCall("expenses/upload-import", "post", formData, true, {}, userData?.token);
            if (response?.status) {
                const fileId = response.data?.file_id ?? response.data?.export_file_id ?? "";
                const key = `import-notif-${Date.now()}`;
                notification.success({
                    key,
                    message: "Import started",
                    description: (
                        <span>
                            {fileId && <><b>{fileId}</b> — </>}
                            {response.message ?? "Your file is being processed."}{" "}
                            <a
                                href="/settings/file-manager?tab=import"
                                style={{ color: "#f45b68", fontWeight: 600 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    notification.destroy(key);
                                    navigate("/settings/file-manager?tab=import");
                                }}
                            >
                                Go to Import & Export
                            </a>
                        </span>
                    ),
                    duration: 0,
                    placement: "top",
                    style: { width: "max-content" },
                });
                setUploadedFile(null);
                getExpensesList();
            } else {
                message.error(response?.message || "Import failed. Please try again.");
            }
        } catch (error) {
            console.error("Expenses import error:", error);
            message.error("Something went wrong while uploading the file.");
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        if (!isSpApiConnected || templateDownloading) return;
        syncActionTimestamp("sv_used_import_export");
        setTemplateDownloading(true);
        try {
            const response = await MakeApiCall("expenses/export", "post", null, true, {}, userData?.token);
            if (response?.status) {
                showExportNotification(response.data, response.message);
            } else {
                message.error(response?.message || "Export failed. Please try again.");
            }
        } catch (err) {
            console.error("Expenses template download error:", err);
            message.error("Something went wrong while starting the export.");
        } finally {
            setTemplateDownloading(false);
        }
    };

    const importMenu = (
        <Menu className="p-2 shadow-sm" style={{ borderRadius: '8px', minWidth: '250px' }}>
            <div className="px-3 py-2 border-bottom">
                <span className="fw-bold text-gray-800 fs-7">Bulk Import Expenses</span>
            </div>
            <Menu.Item key="1" className="py-3" disabled={!isSpApiConnected || templateDownloading} onClick={handleDownloadTemplate}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex flex-column">
                        <span className="fs-7 fw-bold">1. Download Template</span>
                        <span className="text-muted" style={{ fontSize: '10px' }}>Get the expense import file</span>
                    </div>
                    {templateDownloading ? <Spin size="small" /> : <DownloadOutlined className="text-danger fs-5" />}
                </div>
            </Menu.Item>
            <Menu.Divider />
            <div className="px-3 py-2">
                <span className="fs-7 fw-bold d-block mb-2">2. Upload Updated File</span>
                <Upload
                    beforeUpload={handleUpload}
                    fileList={uploadedFile ? [uploadedFile] : []}
                    onRemove={() => setUploadedFile(null)}
                    maxCount={1}
                    disabled={!canMutateExpenses}
                >
                    <Button
                        icon={<CloudUploadOutlined />}
                        disabled={!canMutateExpenses}
                        className="w-100 fs-7 h-40px border-dashed border-danger text-danger bg-light-danger bg-opacity-10"
                    >
                        {uploadedFile ? 'Change File' : 'Choose File'}
                    </Button>
                </Upload>
                {uploadedFile && (
                    <Button
                        size="small"
                        type="primary"
                        danger
                        block
                        className="mt-2"
                        onClick={handleApplyClick}
                        loading={uploading}
                        disabled={uploading || !canMutateExpenses}
                    >
                        {uploading ? 'Processing...' : 'Apply Import'}
                    </Button>
                )}
            </div>
        </Menu>
    );

    // CSS styles for mobile view
    const mobileStyles = `
        .expense-detail-modal .detail-section {
            margin-top: 20px;
        }
        
        .expense-detail-modal .detail-row {
            display: flex;
            padding: 12px 0;
            border-bottom: 1px solid var(--app-border);
        }
        
        .expense-detail-modal .detail-row:last-child {
            border-bottom: none;
        }
        
        .expense-detail-modal .detail-label {
            width: 120px;
            font-weight: 500;
            color: var(--app-text-muted);
            flex-shrink: 0;
        }
        
        .expense-detail-modal .detail-value {
            flex: 1;
            color: var(--app-text);
            word-break: break-word;
        }
        
        .expense-cards-container .text-one {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            white-space: normal;
        }
        
        @media (max-width: 768px) {
            .ant-card-body {
                padding: 12px;
            }
            .all-search {
                width: 100% !important;
                margin-bottom: 10px;
            }
        }
    `;

    return (
        <Wrapper>
            <Helmet><title>Expenses</title></Helmet>
            <SetupStepsBanner userData={userData} />

            <SyncToast visible={isDataSyncing && !syncToastDismissed} onDismiss={() => setSyncToastDismissed(true)} />

            <div style={{ position: 'relative' }}>
                <FeatureLockedOverlay locked={trialExpired || !isSpApiConnected} title="Features Locked" message={trialExpired ? "Your trial has expired. Please renew your subscription to continue." : "Connect your Amazon account to unlock analytics."}/>

                <div className="mb-10 mx-md-n9 mx-n2 px-10 py-5 bg-white filter">
                    <div className="position-relative">
                        <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" />
                        <input type="text" value={searchText} onChange={handleSearch} disabled={!isSpApiConnected} className={`form-control form-control-solid text-dark border border-gray-300 fs-7 ps-12 all-search expenses-search-input ${isMobile ? 'w-100' : 'w-300px'}`} placeholder="Search expenses" />
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Expenses List</h3>
                                <div className="card-toolbar gap-2 flex-wrap align-items-center">
                                    <button className="btn btn-sm btn-danger fs-7 fw-bold" disabled={!canMutateExpenses} onClick={() => { setEditingExpense(null); setIsModalVisible(true); }}>
                                        <PlusOutlined className="me-1" /> Add Expense
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger fs-7 fw-bold d-flex align-items-center gap-2"
                                        disabled={!isSpApiConnected}
                                        onClick={() => setFilterModalOpen(true)}
                                    >
                                        <FilterOutlined />
                                        Filters
                                        {activeFilters.length > 0 && (
                                            <span
                                                className="badge rounded-pill bg-white text-danger"
                                                style={{ fontSize: 10, minWidth: 18, padding: "1px 6px", lineHeight: "14px" }}
                                            >
                                                {activeFilters.length}
                                            </span>
                                        )}
                                    </button>
                                    {!isMobile && (
                                        <Dropdown overlay={columnSelectorOverlay} trigger={['click']} placement="bottomRight">
                                            <Button icon={<SettingOutlined />} className="btn btn-sm btn-danger fs-7 fw-bold d-flex align-items-center" style={{ height: 'auto', border: 'none' }}>
                                                Customize Columns
                                            </Button>
                                        </Dropdown>
                                    )}
                                    {/* <div className="bullet bullet-vertical h-25px bg-gray-300 mx-1"></div> */}
                                    <Dropdown overlay={importMenu} trigger={["click"]} disabled={!canMutateExpenses} placement="bottomRight">
                                        <button className="btn btn-sm btn-light-danger fs-7 fw-bold border border-danger border-opacity-25" disabled={!canMutateExpenses}>
                                            <i className="bi bi-download fs-5 me-1"/> Import Expenses
                                        </button>
                                    </Dropdown>
                                    <button className="btn btn-sm btn-light-danger fs-7 fw-bold border" onClick={handleExport} disabled={exporting || !isSpApiConnected}>
                                        {exporting ? <Spin size="small" /> : <><i className="bi bi-upload fs-6 me-1" /> Export</>}
                                    </button>
                                </div>
                            </div>
                            <div className="card-body py-3 px-3">
                                <div className="table-responsive">
                                    {isMobile ? (
                                        renderCardView()
                                    ) : (
                                        <Table
                                            scroll={{ x: "max-content" }}
                                            columns={columns}
                                            loading={loading}
                                            dataSource={list}
                                            rowKey="id"
                                            pagination={false}
                                            onChange={handleTableChange}
                                            components={{
                                                table: (p) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />,
                                                header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> }
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-body py-2">
                                <ConfigProvider theme={{ token: { colorPrimary: '#f45b68' } }}>
                                    <Pagination 
                                        className="mt-3" 
                                        current={page} 
                                        pageSize={pageSize} 
                                        total={totalPage} 
                                        disabled={!isSpApiConnected} 
                                        showSizeChanger
                                        onChange={(p, s) => { setPage(p); setPageSize(s); }}
                                        showTotal={(t, r) => `${r[0]}-${r[1]} of ${t} items`}
                                        size={isMobile ? 'small' : 'default'}
                                        simple={isMobile}
                                    />
                                </ConfigProvider>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ExpenseFilterModal
                open={filterModalOpen}
                onClose={() => setFilterModalOpen(false)}
                activeFilters={activeFilters}
                addFilter={addFilter}
                updateFilter={updateFilter}
                removeFilter={removeFilter}
                clearFilters={clearFilters}
                categoryFilterOptions={categoryFilterOptions}
                productFilterOptions={productFilterOptions}
                productFilterLoading={productFilterLoading}
                searchProductsForFilter={searchProductsForFilter}
            />

            <AddExpense isModalVisible={isModalVisible} handleCancel={handleCancel} handleSubmit={handleSubmit} marketplaceList={marketplaceList} userData={userData} refreshExpenses={getExpensesList} editingExpense={editingExpense} />
            
            {/* Detail Modal for Mobile */}
            {renderDetailModal()}
            
            {/* Mobile Styles */}
            <style>{mobileStyles}</style>
        </Wrapper>
    );
};

export default Expenses;
